/**
 * Same-origin relative paths only — blocks open redirects after login / push deep links.
 */
export function sanitizeReturnUrl(raw: string | null | undefined, fallback = '/dashboard'): string {
  if (raw == null) return fallback;
  let value = String(raw).trim();
  if (!value) return fallback;
  try {
    value = decodeURIComponent(value);
  } catch {
    return fallback;
  }
  if (!value.startsWith('/') || value.startsWith('//')) return fallback;
  if (value.includes('://')) return fallback;
  if (value.toLowerCase().startsWith('/login')) return fallback;
  return value;
}

const LOGIN_ENTRY_KEY = 'auth_login_entry';
const LOGIN_ENTRY_PATHS = ['/login', '/loginwithu'] as const;
export type LoginEntryPath = (typeof LOGIN_ENTRY_PATHS)[number];

/** Remember which login page the user came through so sign-out / session expiry returns there. */
export function rememberLoginEntry(path: string): void {
  if (!(LOGIN_ENTRY_PATHS as readonly string[]).includes(path)) return;
  try {
    localStorage.setItem(LOGIN_ENTRY_KEY, path);
  } catch {
    /* storage unavailable */
  }
}

export function getLoginEntryPath(): LoginEntryPath {
  try {
    const stored = localStorage.getItem(LOGIN_ENTRY_KEY);
    if (stored && (LOGIN_ENTRY_PATHS as readonly string[]).includes(stored)) {
      return stored as LoginEntryPath;
    }
  } catch {
    /* storage unavailable */
  }
  return '/login';
}

export function buildLoginPathWithReturn(currentPathWithSearch: string): string {
  const loginPath = getLoginEntryPath();
  const safe = sanitizeReturnUrl(currentPathWithSearch, '');
  if (!safe) return loginPath;
  return `${loginPath}?returnUrl=${encodeURIComponent(safe)}`;
}
